import {
  fetchShoppingCartRequest,
  createShoppingCartRequest,
  updateShoppingCartRequest,
  checkoutShoppingCartRequest,
  fetchCustomerShoppingCartsRequest,
  fetchDealerShoppingCartsRequest,
  createAnonymousShoppingCartRequest,
  updateAnonymousShoppingCartRequest,
  fetchAnonymousShoppingCartRequest
} from "../api/index";
import { handleErrorRequest } from "../helpers/api";
import {
  payloadIdentity,
  simplePayloadMutation,
  resetMutation,
  loadingMutation
} from "../helpers/redux";
import _ from "lodash";
import { createActions, handleActions } from "redux-actions";
import { getJWT } from "./auth";
import { createSelector } from "reselect";
import sessionId from "./sessionId";

const shoppingCartState = state => state.shoppingCart;
const getValue = key =>
  createSelector(
    shoppingCartState,
    state => _.get(state, key)
  );

export const getShoppingCart = getValue("shoppingCart");
export const getShoppingCarts = getValue("shoppingCarts");
export const isLoadingFetchShoppingCart = getValue("loadingFetchShoppingCart");
export const isSuccessFetchShoppingCart = getValue("successFetchShoppingCart");
export const getFailureFetchShoppingCart = getValue("failureFetchShoppingCart");
export const isSubmittingShoppingCart = getValue("loadingSubmitShoppingCart");
export const getFailureSubmitShoppingCart = getValue(
  "failureSubmitShoppingCart"
);
export const isSuccessSubmitShoppingCart = getValue(
  "successSubmitShoppingCart"
);

const createdActions = createActions({
  shoppingCart: {
    SUBMIT_SHOPPING_CART_ATTEMPT: null,
    SUBMIT_SHOPPING_CART_FAILURE: payloadIdentity("failMessage"),
    SUBMIT_SHOPPING_CART_SUCCESS: null,
    FETCH_SHOPPING_CART_ATTEMPT: null,
    FETCH_SHOPPING_CART_FAILURE: payloadIdentity("failMessage"),
    FETCH_SHOPPING_CART_SUCCESS: null,
    SET_SHOPPING_CART: payloadIdentity("shoppingCart"),
    SET_SHOPPING_CARTS: payloadIdentity("shoppingCarts"),
    RESET_STATE: payloadIdentity("toOmit")
  }
});

const {
  submitShoppingCartAttempt,
  submitShoppingCartFailure,
  submitShoppingCartSuccess,
  fetchShoppingCartAttempt,
  fetchShoppingCartFailure,
  fetchShoppingCartSuccess,
  setShoppingCart,
  setShoppingCarts
} = createdActions.shoppingCart;

const submitShoppingCart = request => async (dispatch, getState) => {
  dispatch(submitShoppingCartAttempt());
  const { response, error } = await request;
  if (response) {
    dispatch(submitShoppingCartSuccess());
    dispatch(setShoppingCart(response.data));
    return Promise.resolve({ response });
  } else {
    dispatch(submitShoppingCartFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  }
};

const createShoppingCart = ({ customerId, dealerId, data }) => (
  dispatch,
  getState
) => {
  const jwt = getJWT(getState());
  if (jwt) {
    return dispatch(
      submitShoppingCart(
        createShoppingCartRequest({ customerId, dealerId, data, jwt })
      )
    );
  } else {
    _.set(data, "shopping_cart.session_id", sessionId.get());
    return dispatch(
      submitShoppingCart(createAnonymousShoppingCartRequest({ data }))
    );
  }
};
const updateShoppingCart = ({ customerId, dealerId, data }) => (
  dispatch,
  getState
) => {
  const jwt = getJWT(getState());
  if (jwt) {
    return dispatch(
      submitShoppingCart(
        updateShoppingCartRequest({ dealerId, customerId, data, jwt })
      )
    );
  } else {
    return dispatch(
      submitShoppingCart(
        updateAnonymousShoppingCartRequest({ data, sessionId: sessionId.get() })
      )
    );
  }
};
const checkoutShoppingCart = ({ customerId, dealerId, data }) => (
  dispatch,
  getState
) => {
  const jwt = getJWT(getState());
  return dispatch(
    submitShoppingCart(
      checkoutShoppingCartRequest({ customerId, dealerId, data, jwt })
    )
  );
};

const fetchShoppingCart = ({ dealerId, customerId }) => async (
  dispatch,
  getState
) => {
  dispatch(fetchShoppingCartAttempt());
  const jwt = getJWT(getState());
  let request = null;
  if (jwt) {
    request = fetchShoppingCartRequest({
      dealerId,
      customerId,
      jwt
    });
  } else {
    request = fetchAnonymousShoppingCartRequest({ sessionId: sessionId.get() });
  }
  const { response, error } = await request;
  if (response) {
    dispatch(fetchShoppingCartSuccess());
    dispatch(setShoppingCart(response.data));
    return Promise.resolve({ response });
  } else {
    dispatch(fetchShoppingCartFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  }
};

const fetchShoppingCarts = request => params => async (dispatch, getState) => {
  dispatch(fetchShoppingCartAttempt());
  const jwt = getJWT(getState());
  const { response, error } = await request({
    ...params,
    jwt
  });
  if (response) {
    dispatch(fetchShoppingCartSuccess());
    dispatch(setShoppingCarts(response.data));
    return Promise.resolve({ response });
  } else {
    dispatch(fetchShoppingCartFailure(handleErrorRequest(error)));
    return Promise.resolve({ error });
  }
};

const fetchDealerShoppingCarts = fetchShoppingCarts(
  fetchDealerShoppingCartsRequest
);
const fetchCustomerShoppingCarts = fetchShoppingCarts(
  fetchCustomerShoppingCartsRequest
);

export const initialState = {
  loadingSubmitShoppingCart: false,
  failureSubmitShoppingCart: null,
  successSubmitShoppingCart: false,
  loadingFetchShoppingCart: false,
  failureFetchShoppingCart: null,
  successFetchShoppingCart: false,
  shoppingCart: null,
  shoppingCarts: []
};

export const actions = {
  ...createdActions.shoppingCart,
  createShoppingCart,
  fetchShoppingCart,
  updateShoppingCart,
  checkoutShoppingCart,
  fetchCustomerShoppingCarts,
  fetchDealerShoppingCarts
};

export default handleActions(
  {
    shoppingCart: {
      SUBMIT_SHOPPING_CART_ATTEMPT: loadingMutation({
        name: "SubmitShoppingCart",
        type: "loading"
      }),
      SUBMIT_SHOPPING_CART_FAILURE: loadingMutation({
        name: "SubmitShoppingCart",
        type: "failure"
      }),
      SUBMIT_SHOPPING_CART_SUCCESS: loadingMutation({
        name: "SubmitShoppingCart",
        type: "success"
      }),
      FETCH_SHOPPING_CART_ATTEMPT: loadingMutation({
        name: "FetchShoppingCart",
        type: "loading"
      }),
      FETCH_SHOPPING_CART_FAILURE: loadingMutation({
        name: "FetchShoppingCart",
        type: "failure"
      }),
      FETCH_SHOPPING_CART_SUCCESS: loadingMutation({
        name: "FetchShoppingCart",
        type: "success"
      }),
      SET_SHOPPING_CART: simplePayloadMutation,
      SET_SHOPPING_CARTS: simplePayloadMutation,
      RESET_STATE: resetMutation(initialState)
    }
  },
  initialState
);
